import { HelpCircle, X } from "lucide-react";
import { ReactNode, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface SettingCardProps {
  title: string;
  description?: string;
  helpText?: string;
  helpDetails?: ReactNode;
  icon?: ReactNode;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "destructive" | "outline";
  className?: string;
  children: ReactNode;
}

export function SettingCard({
  title,
  description,
  helpText,
  helpDetails,
  icon,
  badge,
  badgeVariant = "secondary",
  className,
  children,
}: SettingCardProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Check if device has touch capability
  const isTouchDevice = typeof window !== "undefined" && "ontouchstart" in window;

  const hasHelp = helpText || helpDetails || description;

  const helpIcon = hasHelp ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "h-auto p-1 text-muted-foreground hover:text-foreground",
        // Larger tap target on mobile
        isTouchDevice && "min-h-[44px] min-w-[44px] -m-2"
      )}
      onClick={() => setIsPopoverOpen(!isPopoverOpen)}
    >
      <HelpCircle className="h-4 w-4" />
      <span className="sr-only">Show help for {title}</span>
    </Button>
  ) : null;

  const helpContent = hasHelp ? (
    <div className="max-w-sm space-y-3">
      {description && (
        <div className="text-sm text-muted-foreground leading-relaxed">{description}</div>
      )}

      {helpText && <div className="text-sm text-muted-foreground leading-relaxed">{helpText}</div>}

      {helpDetails && <div className="text-sm text-muted-foreground">{helpDetails}</div>}

      <Button
        variant="ghost"
        size="sm"
        className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground p-1"
        onClick={() => setIsPopoverOpen(false)}
      >
        <X className="h-3 w-3" />
        Close
      </Button>
    </div>
  ) : null;

  const headerContent = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        {icon && <div className="flex-shrink-0">{icon}</div>}

        <div className="flex items-center gap-2">
          <CardTitle className="text-lg font-medium">{title}</CardTitle>

          {badge && (
            <Badge variant={badgeVariant} className="text-xs">
              {badge}
            </Badge>
          )}
        </div>
      </div>

      {helpIcon && (
        <div className="flex-shrink-0">
          {isTouchDevice ? (
            // Mobile: Popover only
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>{helpIcon}</PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end" side="bottom" sideOffset={4}>
                {helpContent}
              </PopoverContent>
            </Popover>
          ) : (
            // Desktop: Tooltip + clickable popover
            <TooltipProvider>
              <Tooltip>
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <TooltipTrigger asChild>{helpIcon}</TooltipTrigger>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="end" side="bottom" sideOffset={4}>
                    {helpContent}
                  </PopoverContent>
                </Popover>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">Click for detailed help</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Card className={cn("border-0 shadow-sm", className)}>
      <CardHeader className="pb-4">{headerContent}</CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-4">{children}</div>
      </CardContent>
    </Card>
  );
}
